import {
  canCreateEncounters,
  canDeletePatientRecords,
  canModifyPatientRecords,
} from "../utils/user";

import { ClientMode } from "./system";

import type { FacilityEntity } from "../scenes/Facility/models/facilities";
import type { ProfessionalCodingType } from "../scenes/FacilityPreferences/models/preferences";
import type { IdDescriptionBase } from "./patientEncounter";
import type { DecodeTokenResultEntity, FacilityMap } from ".";

export enum UserRole {
  ADMIN = 400,
  POWER = 300,
  CODER = 200,
  MANAGER = 100,
  NOROLE = 0,
}

export interface UserState {
  // new props
  allAvailablePermissions: string;
  commonPermissionsInAllFacilities: string;
  isSystemAdmin: boolean;
  isItAdmin: boolean;
  highestRole: UserRole;
  clientMode: ClientMode;
  facilityRoles?: {
    facilityId: string;
    role: UserRole;
  };
  privileges: UserPrivileges;

  account: EncoderUserEntity;
  avatarText: string;
  loading: boolean;
  loaded: boolean;

  preferences: UserPreferences;
  loadingPreferences: boolean;

  dirty: boolean;

  // old props (cca era)
  error: boolean;
  errorDetail: string;
  errorReason: string;
  isAdviceAdmin: boolean;
  isPartnerAdviceAdmin: boolean;
  token: string;
  tokenDecoded: DecodeTokenResultEntity | null;
  id: string | null;
  facilities: FacilityMap;
  name: string | null;
  organizationName: string | null;
  organizationId: string | null;
}

// TODO: Consider making this hierarchical, e.g.
// encounter {
//  canSearch
//  ..
// },
// settings {
//   canSystem
//   ..
// }
export interface UserPrivileges {
  canSearch: boolean;
  canCreate: boolean;
  // TODO: consider merging these two. for the beta/calculator mode they will not be one in the same
  // but for other environments (interface/standard) i believe they are synonymous
  canSaveInterfaced: boolean;
  canSaveNotInterfaced: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canCustomizeSettings: boolean;
}

export const getUserPrivileges = (
  state?: UserState,
  encounterPermissionsOrRole?: string | UserRole,
): UserPrivileges => {
  if (!state) {
    return {
      canSearch: false,
      canSaveInterfaced: false,
      canSaveNotInterfaced: false,
      canEdit: false,
      canDelete: false,
    } as UserPrivileges;
  }

  switch (state.clientMode) {
    case ClientMode.Basic: {
      return {
        canSaveInterfaced: false,
        // WEB-4611
        canSaveNotInterfaced: true,
        canSearch: false,
        // per requirements, lower the standard role requirement for creating/editing since they can't save
        // canCreate: state.isSystemAdmin || state.highestRole !== UserRole.NOROLE,
        // WEB-3606 - enable
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canCustomizeSettings: false,
      };
    }

    case ClientMode.Interface: {
      return {
        canSearch: false,
        // WEB-3606 - enable
        canCreate: true,
        canSaveInterfaced: true,
        canSaveNotInterfaced: false,
        canEdit: true,
        canDelete: true,
        canCustomizeSettings: state.isSystemAdmin || state.highestRole >= UserRole.CODER,
      };
    }

    case ClientMode.Standard:
    default: {
      let canEditOrSave = state.isSystemAdmin;
      let canDelete = state.isSystemAdmin;

      if (!state.isSystemAdmin) {
        // Check if encounterPermissionsOrRole is a permissions string or a UserRole enum
        if (typeof encounterPermissionsOrRole === "string") {
          // It's a permissions string - check for ModifyPatientRecord permission
          canEditOrSave = canModifyPatientRecords(encounterPermissionsOrRole);
          canDelete = canDeletePatientRecords(encounterPermissionsOrRole);
        } else if (typeof encounterPermissionsOrRole === "number") {
          // It's a UserRole enum - use role-based check
          canEditOrSave = encounterPermissionsOrRole >= UserRole.CODER;
          canDelete = encounterPermissionsOrRole >= UserRole.POWER;
        } else {
          // No encounter info - use highest role
          canEditOrSave = state.highestRole >= UserRole.CODER;
          canDelete = state.highestRole >= UserRole.POWER;
        }
      }

      return {
        canSearch: state.isSystemAdmin || state.highestRole >= UserRole.MANAGER,
        canCreate: state.isSystemAdmin || state.highestRole >= UserRole.CODER,
        canSaveInterfaced: canEditOrSave,
        canSaveNotInterfaced: canEditOrSave,
        canDelete,
        canEdit: canEditOrSave,
        canCustomizeSettings: state.isSystemAdmin || state.highestRole >= UserRole.CODER,
      };
    }
  }
};

export const USER_FEATURES = {
  ProFee: "ProFee",
  FutureDates: "FutureDates",
  FAC: "FAC",
  PSA: "PSA",
  STU: "STU",
} as const;

export type UserFeature = (typeof USER_FEATURES)[keyof typeof USER_FEATURES];

export type UserFeaturesAvailablePackages = Record<UserFeature, boolean>;

export interface UserFeaturesState {
  loading: boolean;
  error: string;
  packages: null | UserFeaturesAvailablePackages;
}

export enum UserScope {
  PARTNER = "Partner",
  PERSONAL = "Personal",
  FACILITY = "Facility",
  ALL = "All",
  UNKNOWN = "Unknown",
}

export type DefaultRoleFieldName = "defaultFacilityRole" | "defaultPracticeRole";

export interface EncoderUserEntity {
  id: string;
  clientId: string;
  customerId: string;
  userId: string;
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  status: UserStatus;
  version: string;
  isSystemAdmin: boolean;
  isItAdmin: boolean;
  roles?: FacilityRolesEntity[];
  practiceRoles?: FacilityRolesEntity[];
  invitationLink?: string;
  hasMoreChunks?: boolean;
  mailSent?: boolean;
  originalOrganizationId?: string;
  errorDetails?: string;
  licensePackageId?: string;
  defaultFacilityRole?: string | null;
  defaultPracticeRole?: string | null;
}

export interface ManageEncoderUserState {
  selectedAccount: EncoderUserEntity;
  errorText: string;
  inviteErrorTexts?: string[];
  inviteSuccesses?: string[];
  loading: boolean;
  dirty: boolean;
  userInfoDirty: boolean;
  facilities?: FacilityEntity[];
  practices?: FacilityEntity[];
}

export const UserRoleMap: Record<string, UserRole> = {
  FacilityAdmin: UserRole.ADMIN,
  PracticeAdmin: UserRole.ADMIN,
  PowerCoder: UserRole.POWER,
  OfficeManager: UserRole.POWER,
  Coder: UserRole.CODER,
  CoderBiller: UserRole.CODER,
  CaseManager: UserRole.MANAGER,
  NoRole: UserRole.NOROLE,
};

export interface ManageUserRole {
  Role: string;
  Label: string;
}
export type UserRolesSet = ManageUserRole[];

export const USER_NO_ROLE = "NoRole";

export interface CurrentUserEntityRoles {
  facilityId: string;
  role: UserRole;
}

export interface CurrentUserEntity {
  allAvailablePermissions: string;
  commonPermissionsInAllFacilities: string;
  isItAdmin: boolean;
  isSystemAdmin: boolean;
  highestRole: UserRole;
  roles?: CurrentUserEntityRoles[];
  clientMode: ClientMode;
  organizationName?: string;
}

export interface CurrentUserServiceResponse {
  AllAvailablePermissions: string;
  CommonPermissionsInAllFacilities: string;
  IsItAdmin: boolean;
  IsSystemAdmin: boolean;
  HighestRole: string;
  ClientMode: string | null;
  Roles?: {
    FacilityId: string;
    Role: string;
  }[];
}

export interface FacilityRolesEntity {
  AllowProfessionalCoding?: boolean;
  AllowProfessionalCodingForFacility?: ProfessionalCodingType;
  Facility: string;
  FacilityId: string;
  Role: string;
  UserId: string;
  Version: string;
  FacilityIsActive: boolean;
}

export interface EncoderUserListState {
  totalUsers: number;
  users: EncoderUserEntity[];
  errorText: string;
  loading: boolean;
}

export interface EncoderUserListResponseEntity {
  count: number;
  total_count: number;
  users: EncoderUser[];
}

export interface EncoderUserListResultEntity {
  count: number;
  total_count: number;
  users: EncoderUserEntity[];
}

export interface EncoderUser {
  ClientId: string;
  CustomerId: string;
  UserId: string;
  FirstName: string;
  LastName: string;
  UserName: string;
  Email: string;
  IsItAdmin: boolean;
  IsSystemAdmin: boolean;
  Status: UserStatus;
  Version: string;
  InvitationLink?: string;
  LicensePackageId?: string;
  MailSent?: boolean;
  OriginalOrganizationId?: string;
  ErrorDetails?: string;
  DefaultFacilityRole?: string | null;
  DefaultPracticeRole?: string | null;
}

export enum UserStatus {
  INVITED = "Invited",
  ACTIVE = "Active",
  DISABLED = "Disabled",
}

export enum SyncStatus {
  PENDING = "Pending",
  CONSISTENT = "Consistent",
}

export interface UserPreferencesEntity {
  key: string;
  value: object;
}

export interface UserPreferences {
  [key: string]: string | boolean | number | object;

  HideExternalLinkWarning: boolean;
  AnimationDisabled: boolean;
}

export const canCreateEncountersForDomain = (permissions?: string): boolean =>
  canCreateEncounters(permissions);

export const getAvailableDomainEntities = (
  allEntities: IdDescriptionBase[],
  currentEntityId?: string,
): IdDescriptionBase[] => {
  if (!allEntities?.length) {
    return [];
  }

  return allEntities.filter(
    (entity) =>
      entity.id === currentEntityId || canCreateEncountersForDomain(entity.CurrentUserPermissions),
  );
};
